import { signInWithCustomToken } from 'firebase/auth'
import { useSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useAuthEmailPasswordMutation } from 'src/api/mutations'
import {
  useAuthPasswordResetFinishMutation,
  useAuthSecondFactorMutation,
} from 'src/api/mutations/signIn'
import { useMeLazyQuery } from 'src/api/queries/users/useMeQuery/useMeLazyQuery'
import { useUserProvidersGetLazy } from 'src/api/queries/users/useUserProvidersQuery/useUserProvidersGetLazyQuery'
import { signInReturnTake } from 'src/api/signInReturn'
import { auth } from 'src/firebase'
import { invitationLinkTokenForget } from 'src/pages/InvitationPage/invitationLinkToken'
import { resetLinkTokenForget } from 'src/pages/PasswordResetPage/resetLinkToken'
import { authSlice } from 'src/redux/slices'
import { graphqlErrorCode } from 'src/utils/graphqlErrorCode'

// ----------------------------------------------------------------------
// Sign in, both ways.
//
// A failure is returned rather than thrown at a corner of the screen. The
// email path stays on the page, so the form states it directly. The Tesla
// path leaves the console and comes back to a page that has not mounted
// yet, so its reason travels in router state and the login page reads it
// off the location.
//
// The email path takes two steps. The password proves itself and hands back
// a ticket; the ticket and a code from the authenticator app, or a recovery
// code, start the session. A password never signs anybody in alone.
//
// A reset link finishes the same way: the link, a new password and the code
// start the session, so where somebody lands afterwards is decided here once:
// the page they were opening when they were sent to sign in
// (`signInReturnTake`), or the console's home.
// ----------------------------------------------------------------------

/** The second step's answer: a code from the app, or a recovery code. */
export type SecondFactorAnswer = { code: string } | { recoveryCode: string }

/** Refusals after which the ticket is gone and the password is asked again. */
const TICKET_OVER = new Set(['SIGN_IN_EXPIRED', 'RATE_LIMITED'])

/**
 * Where a ticket came from: the password step, or a Tesla sign-in that owed
 * the account's code before linking the Tesla account. The server's words for
 * a spent ticket send somebody back to their password, which is not where a
 * Tesla sign-in starts again.
 */
export type SecondFactorVia = 'password' | 'tesla' | 'google' | 'apple'

const PROVIDER_NAME = {
  tesla: 'Tesla',
  google: 'Google',
  apple: 'Apple',
} as const

type ProviderVia = keyof typeof PROVIDER_NAME

const ticketExpired = (via: ProviderVia) =>
  `That sign-in has expired. Continue with ${PROVIDER_NAME[via]} again.`

const ticketSpent = (via: ProviderVia) =>
  `That code is not right, and that was the last try. Continue with ${PROVIDER_NAME[via]} again.`

/** How a reset from a link ended; `linkGone` needs a new link to go on. */
export type PasswordResetOutcome = 'done' | 'linkGone' | 'failed'

const attemptsLeftOf = (error: unknown): unknown =>
  (error as { graphQLErrors?: { extensions?: { attemptsLeft?: unknown } }[] })
    ?.graphQLErrors?.[0]?.extensions?.attemptsLeft

const useLogin = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | undefined>()
  const [getMe] = useMeLazyQuery()
  const [userProviderGet] = useUserProvidersGetLazy()
  const [authEmailPassword] = useAuthEmailPasswordMutation()
  const [authSecondFactor] = useAuthSecondFactorMutation()
  const [authPasswordResetFinish] = useAuthPasswordResetFinishMutation()
  const [secondFactorTicket, setSecondFactorTicket] = useState<string | null>(
    null,
  )
  const [secondFactorVia, setSecondFactorVia] =
    useState<SecondFactorVia>('password')
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  /**
   * Everything a Tesla sign-in does once the backend has answered with a
   * console session: whichever way the code reached the backend.
   */
  const teslaSignInComplete = useCallback(
    async (
      loginToken: string,
      invitation: 'accepted' | 'unavailable' | null | undefined,
      fromInvitation: boolean,
    ) => {
      // The invitation is dealt with either way: accepted, or past saving.
      if (fromInvitation) {
        invitationLinkTokenForget()
        if (invitation === 'unavailable') {
          enqueueSnackbar(
            'You are signed in, but that invitation had expired or was already used. Ask for it to be sent again.',
            { variant: 'warning' },
          )
        }
      }

      let userCredential

      try {
        userCredential = await signInWithCustomToken(auth, loginToken)
      } catch (error) {
        console.error(error)
        setLoading(false)
        navigate('/login', { replace: true, state: { error: error.message } })
        return
      }

      try {
        const me = await getMe()
        const providers = (await userProviderGet()) ?? []

        // What the server says, not `true`. Hard-coded, it showed every new
        // manager Beast staff navigation until `AuthProvider` caught up.
        dispatch(
          authSlice.actions.setAuthState({
            isDeterminate: true,
            user: userCredential.user,
            isAdmin: !!me?.isAdmin,
          }),
        )

        setLoading(false)

        if (!providers.length) {
          navigate('/onboarding', { replace: true })
        } else {
          // The page the person was opening before they signed in, if any.
          navigate(signInReturnTake(), { replace: true })
        }
        return
      } catch (error) {
        setLoading(false)
        console.error(error)
        navigate('/login', { replace: true, state: { error: error?.message } })
      }
    },
    [getMe, navigate, dispatch, userProviderGet, enqueueSnackbar],
  )

  /** Signs in to Firebase with the session the backend minted, then routes. */
  const sessionStart = useCallback(
    async (loginToken: string) => {
      let userCredential

      try {
        userCredential = await signInWithCustomToken(auth, loginToken)
      } catch (error) {
        console.error(error)
        setError(error.message)
        setLoading(false)
        return
      }

      const me = await getMe()
      const providers = (await userProviderGet()) ?? []

      dispatch(
        authSlice.actions.setAuthState({
          isDeterminate: true,
          user: userCredential.user,
          isAdmin: !!me?.isAdmin,
        }),
      )

      setLoading(false)

      if (!providers.length) {
        navigate('/onboarding', { replace: true })
      } else {
        navigate(signInReturnTake(), { replace: true })
      }
    },
    [getMe, navigate, dispatch, userProviderGet],
  )

  /** Says a recovery code was used, and how many are left, when one was. */
  const recoveryCodesNotice = useCallback(
    (left: number | null | undefined) => {
      if (typeof left !== 'number') {
        return
      }
      enqueueSnackbar(
        left === 0
          ? 'That was your last recovery code. Make new ones in Settings now.'
          : `Recovery code used. ${String(
              left,
            )} left: you can make new ones in Settings.`,
        { variant: left <= 3 ? 'warning' : 'info' },
      )
    },
    [enqueueSnackbar],
  )

  const emailLogin = useCallback(
    async (email: string, password: string) => {
      setLoading(true)
      setError(undefined)

      try {
        const response = await authEmailPassword({
          variables: {
            input: {
              email,
              password,
            },
          },
        })

        const ticket = response.data?.authEmailPassword.secondFactorTicket
        if (!ticket) {
          setError('That email and password do not match an account.')
          setLoading(false)
          return
        }

        setSecondFactorTicket(ticket)
        setLoading(false)
      } catch (error) {
        setLoading(false)
        console.error(error)
        setError(error?.message ?? 'Sign in failed. Please try again.')
      }
    },
    [authEmailPassword],
  )

  const secondFactorLogin = useCallback(
    async (answer: SecondFactorAnswer) => {
      if (!secondFactorTicket) {
        return
      }
      setLoading(true)
      setError(undefined)

      try {
        const response = await authSecondFactor({
          variables: { input: { secondFactorTicket, ...answer } },
        })

        const signedIn = response.data?.authSecondFactor
        if (!signedIn?.token) {
          setError('Sign in failed. Please try again.')
          setLoading(false)
          return
        }

        recoveryCodesNotice(signedIn.recoveryCodesRemaining)

        setSecondFactorTicket(null)
        await sessionStart(signedIn.token)
      } catch (error) {
        setLoading(false)
        console.error(error)
        const code = graphqlErrorCode(error)
        const spent = attemptsLeftOf(error) === 0
        if ((code && TICKET_OVER.has(code)) || spent) {
          setSecondFactorTicket(null)
        }
        if (secondFactorVia !== 'password' && code === 'SIGN_IN_EXPIRED') {
          setError(ticketExpired(secondFactorVia))
        } else if (secondFactorVia !== 'password' && spent) {
          setError(ticketSpent(secondFactorVia))
        } else {
          setError(error?.message ?? 'Sign in failed. Please try again.')
        }
      }
    },
    [
      authSecondFactor,
      secondFactorTicket,
      secondFactorVia,
      sessionStart,
      recoveryCodesNotice,
    ],
  )

  /**
   * Takes over a ticket handed across a page: a Tesla sign-in that owes the
   * account's authenticator code before its Tesla account is linked.
   */
  const secondFactorContinue = useCallback(
    (ticket: string, via: SecondFactorVia) => {
      setSecondFactorTicket(ticket)
      setSecondFactorVia(via)
      setError(undefined)
    },
    [],
  )

  /** A reset from its emailed link: the new password and the second factor. */
  const passwordResetLogin = useCallback(
    async (
      token: string,
      password: string,
      answer: SecondFactorAnswer,
    ): Promise<PasswordResetOutcome> => {
      setLoading(true)
      setError(undefined)

      try {
        const response = await authPasswordResetFinish({
          variables: { input: { token, password, ...answer } },
        })

        const signedIn = response.data?.authPasswordResetFinish
        if (!signedIn?.token) {
          setError('The reset did not go through. Please try again.')
          setLoading(false)
          return 'failed'
        }

        resetLinkTokenForget()
        enqueueSnackbar(
          'Password changed. Every other session on this account is signed out.',
          { variant: 'success' },
        )
        recoveryCodesNotice(signedIn.recoveryCodesRemaining)
        await sessionStart(signedIn.token)
        return 'done'
      } catch (error) {
        setLoading(false)
        console.error(error)
        setError(
          error?.message ?? 'The reset did not go through. Please try again.',
        )
        if (
          graphqlErrorCode(error) === 'RESET_LINK_UNAVAILABLE' ||
          attemptsLeftOf(error) === 0
        ) {
          resetLinkTokenForget()
          return 'linkGone'
        }
        return 'failed'
      }
    },
    [
      authPasswordResetFinish,
      sessionStart,
      enqueueSnackbar,
      recoveryCodesNotice,
    ],
  )

  /** Back to the email and password, dropping the ticket. */
  const startOver = useCallback(() => {
    setSecondFactorTicket(null)
    setSecondFactorVia('password')
    setError(undefined)
  }, [])

  return {
    teslaSignInComplete,
    emailLogin,
    secondFactorLogin,
    passwordResetLogin,
    sessionStart,
    startOver,
    secondFactorContinue,
    secondFactorTicket,
    secondFactorVia,
    loading,
    error,
  }
}

export { useLogin }
